import { useMemo, useRef, useState } from 'react';
import { DeleteOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import { Image, Tag, Upload } from 'antd';
import type { UploadProps } from 'antd/es/upload/interface';
import {
  mergeProductImages,
  splitProductImages,
  type ProductImagesValue,
} from '../productImages';

const DEFAULT_MAX_BYTES = 20 * 1024 * 1024;
const TILE_SIZE = 104;

export interface ProductImageEditorProps {
  value: ProductImagesValue;
  onChange: (value: ProductImagesValue) => void;
  uploadFile?: (file: File) => Promise<string>;
  maxBytes?: number;
  onError?: (message: string) => void;
}

export function ProductImageEditor({
  value,
  onChange,
  uploadFile,
  maxBytes = DEFAULT_MAX_BYTES,
  onError,
}: ProductImageEditorProps) {
  const urls = useMemo(
    () => mergeProductImages(value.mainImage, value.galleryImageUrls),
    [value.galleryImageUrls, value.mainImage],
  );
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);

  const applyUrls = (nextUrls: string[]) => {
    onChange(splitProductImages(nextUrls));
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const next = [...urls];
    const [item] = next.splice(fromIndex, 1);
    if (item == null) return;
    next.splice(toIndex, 0, item);
    applyUrls(next);
  };

  const handleRemove = (index: number) => {
    if (urls.length <= 1) {
      onError?.('至少保留一张封面图');
      return;
    }
    applyUrls(urls.filter((_, i) => i !== index));
  };

  const validateImageFile = (file: File): boolean => {
    if (!file.type.startsWith('image/')) {
      onError?.('请上传图片文件');
      return false;
    }
    if (file.size > maxBytes) {
      onError?.('单文件不超过 20MB');
      return false;
    }
    return true;
  };

  const customRequest: UploadProps['customRequest'] | undefined = uploadFile
    ? async (options) => {
        const { file, onError: uploadOnError, onSuccess } = options;
        try {
          const imageFile = file as File;
          if (!validateImageFile(imageFile)) {
            uploadOnError?.(new Error('invalid'));
            return;
          }
          setUploading(true);
          const publicUrl = await uploadFile(imageFile);
          applyUrls([...urls, publicUrl]);
          onSuccess?.(publicUrl);
        } catch (error) {
          uploadOnError?.(error as Error);
          onError?.(error instanceof Error ? error.message : '上传失败');
        } finally {
          setUploading(false);
        }
      }
    : undefined;

  return (
    <div>
      <div style={{ marginBottom: 8, color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>
        拖拽调整顺序；第一张为封面，可将任意附图拖到首位设为封面。至少保留一张封面图。
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {urls.map((url, index) => (
          <div
            key={`${url}-${index}`}
            draggable
            onDragStart={() => {
              dragIndexRef.current = index;
            }}
            onDragEnd={() => {
              dragIndexRef.current = null;
              setDragOverIndex(null);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDragOverIndex(index);
            }}
            onDragLeave={() => {
              setDragOverIndex((current) => (current === index ? null : current));
            }}
            onDrop={(event) => {
              event.preventDefault();
              const fromIndex = dragIndexRef.current;
              dragIndexRef.current = null;
              setDragOverIndex(null);
              if (fromIndex == null) return;
              handleReorder(fromIndex, index);
            }}
            style={{
              position: 'relative',
              width: TILE_SIZE,
              height: TILE_SIZE,
              border:
                dragOverIndex === index
                  ? '2px solid #1677ff'
                  : '1px solid #d9d9d9',
              borderRadius: 8,
              overflow: 'hidden',
              cursor: 'grab',
              background: '#fafafa',
            }}
          >
            <Image
              src={url}
              width={TILE_SIZE}
              height={TILE_SIZE}
              preview
              style={{ objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
            />
            {index === 0 ? (
              <Tag
                color="gold"
                style={{
                  position: 'absolute',
                  left: 4,
                  top: 4,
                  margin: 0,
                  fontSize: 11,
                  lineHeight: '18px',
                }}
              >
                封面
              </Tag>
            ) : null}
            <HolderOutlined
              style={{
                position: 'absolute',
                right: 4,
                bottom: 4,
                color: '#fff',
                background: 'rgba(0,0,0,0.45)',
                borderRadius: 4,
                padding: 2,
                fontSize: 12,
              }}
            />
            <button
              type="button"
              aria-label="删除图片"
              onClick={() => handleRemove(index)}
              style={{
                position: 'absolute',
                right: 4,
                top: 4,
                border: 'none',
                borderRadius: 4,
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0,0,0,0.55)',
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              <DeleteOutlined style={{ fontSize: 12 }} />
            </button>
          </div>
        ))}
        {uploadFile ? (
          <Upload
            accept="image/*"
            showUploadList={false}
            customRequest={customRequest}
            disabled={uploading}
          >
            <div
              style={{
                width: TILE_SIZE,
                height: TILE_SIZE,
                border: '1px dashed #d9d9d9',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(0,0,0,0.45)',
                cursor: uploading ? 'not-allowed' : 'pointer',
                background: '#fafafa',
              }}
            >
              <PlusOutlined />
              <div style={{ marginTop: 8, fontSize: 12 }}>{uploading ? '上传中' : '上传图片'}</div>
            </div>
          </Upload>
        ) : null}
      </div>
    </div>
  );
}
